import type {
  Avatar,
  ControlMode,
  PlaybackState,
  RoomSummary,
  RoomSettings,
  StickerPack,
} from '@kinop/shared';

/**
 * Обычные HTTP-запросы к серверу. Всё, что происходит во время просмотра,
 * идёт через сокет; здесь только создание комнаты и загрузка файлов.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }

  if (!response.ok) {
    // Сервер отвечает JSON, но за прокси может прилететь и HTML-страница ошибки.
    let code = 'internal';
    let message = `Ошибка ${response.status}`;
    try {
      const body = (await response.json()) as { error?: string; message?: string };
      code = body.error ?? code;
      message = body.message ?? message;
    } catch {
      /* оставляем сообщение по умолчанию */
    }
    throw new ApiError(response.status, code, message);
  }

  return (await response.json()) as T;
}

/** Токен входа живёт здесь же, рядом с остальным, что помнит браузер. */
const TOKEN_KEY = 'kinop:tg-token';

export function getTelegramToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setTelegramToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* приватный режим — вход просто не запомнится */
  }
}

function withToken(): Record<string, string> {
  const token = getTelegramToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}


export interface CreateRoomResult {
  room: RoomSummary;
  playback: PlaybackState;
  hostToken: string;
  joinUrl: string;
}

export function createRoom(params: {
  name: string;
  controlMode: ControlMode;
  settings?: Partial<RoomSettings>;
}): Promise<CreateRoomResult> {
  return request<CreateRoomResult>('/api/rooms', {
    method: 'POST',
    // Токен нужен там, где вход обязателен. Где нет — сервер его не смотрит.
    headers: withToken(),
    body: JSON.stringify(params),
  });
}

export interface RoomPreview {
  code: string;
  name: string;
  isClosed: boolean;
  controlMode: ControlMode;
  sourceTitle: string | null;
  hasSource: boolean;
  participantCount: number;
}

export function getRoomPreview(code: string): Promise<RoomPreview> {
  return request<RoomPreview>(`/api/rooms/${encodeURIComponent(code)}`);
}

export interface UploadTicket {
  uploadId: string;
  chunkSize: number;
  totalChunks: number;
  receivedChunks: number[];
}

export function createUpload(params: {
  fileName: string;
  sizeBytes: number;
  mimeType: string;
  clientId: string;
}): Promise<UploadTicket> {
  return request<UploadTicket>('/api/uploads', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

export interface UploadStatus {
  uploadId: string;
  status: 'pending' | 'complete' | 'failed';
  chunkSize: number;
  totalChunks: number;
  receivedChunks: number[];
  sizeBytes: number;
  url: string | null;
}

export function getUploadStatus(uploadId: string): Promise<UploadStatus> {
  return request<UploadStatus>(`/api/uploads/${uploadId}`);
}

export function completeUpload(uploadId: string): Promise<{
  source: { kind: 'upload'; url: string; format: 'progressive'; title: string; durationMs: null };
}> {
  return request(`/api/uploads/${uploadId}/complete`, { method: 'POST' });
}

// ─────────────────────────── открытые комнаты ───────────────────────────

export interface OpenRoom {
  durationMs?: number | null;
  code: string;
  name: string;
  title: string | null;
  isPlaying: boolean;
  positionMs: number;
  participantCount: number;
  participants: { nickname: string; avatar: Avatar | null }[];
  createdAt: number;
}

export function listOpenRooms(): Promise<{ rooms: OpenRoom[] }> {
  return request<{ rooms: OpenRoom[] }>('/api/rooms/open');
}

// ─────────────────────────── телеграм ───────────────────────────

export interface TelegramConfig {
  enabled: boolean;
  botUsername: string | null;
  /** Без входа через телеграм на сайт не пускают. */
  required: boolean;
}

export interface TelegramUser {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
  /** Нажат ли «Старт» боту: без этого уведомления не дойдут. */
  botStarted: boolean;
  notifyTelegram: boolean;
}

export function getTelegramConfig(): Promise<TelegramConfig> {
  return request<TelegramConfig>('/api/telegram/config');
}

export async function loginWithTelegram(
  payload: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<TelegramUser> {
  const result = await request<{ token: string; user: TelegramUser }>('/api/telegram/login', {
    method: 'POST',
    signal,
    body: JSON.stringify(payload),
  });
  setTelegramToken(result.token);
  return result.user;
}

export async function getTelegramMe(): Promise<TelegramUser | null> {
  if (!getTelegramToken()) return null;
  const result = await request<{ user: TelegramUser | null }>('/api/telegram/me', {
    headers: withToken(),
  });
  // Токен мог протухнуть за полгода — тогда просто забываем его.
  if (!result.user) setTelegramToken(null);
  return result.user;
}

export async function logoutTelegram(): Promise<void> {
  const token = getTelegramToken();
  setTelegramToken(null);
  if (!token) return;
  await request('/api/telegram/logout', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  }).catch(() => undefined);
}

export function listStickerPacks(): Promise<{ packs: StickerPack[]; enabled: boolean }> {
  return request<{ packs: StickerPack[]; enabled: boolean }>('/api/telegram/stickers');
}

/** Один набор: окну из чата весь каталог ни к чему. */
export function getStickerPack(name: string): Promise<{ pack: StickerPack }> {
  return request<{ pack: StickerPack }>(`/api/telegram/stickers/${encodeURIComponent(name)}`);
}

export function importStickerPack(pack: string): Promise<{ pack: StickerPack | null }> {
  return request<{ pack: StickerPack | null }>('/api/telegram/stickers', {
    method: 'POST',
    headers: withToken(),
    body: JSON.stringify({ pack }),
  });
}

/** Ссылка на файл стикера. Идёт через наш сервер: в адресе телеграма — токен бота. */
export function stickerUrl(id: string): string {
  return `/api/telegram/sticker/${encodeURIComponent(id)}`;
}

export function sendRoomToTelegram(
  code: string,
  name: string,
): Promise<{ sent: boolean; reason: string | null; botUsername?: string | null }> {
  return request<{ sent: boolean; reason: string | null; botUsername?: string | null }>(
    '/api/telegram/notify',
    { method: 'POST', headers: withToken(), body: JSON.stringify({ code, name }) },
  );
}
